import React from "react";
import { motion } from "framer-motion";
import { Download, FileText, Sparkles, ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import Hometechused4 from "./home/home-techused-fourth";
import WebServices from "../plans/webservices";
import AppDevelopment from "../plans/AppDevelopment";
import Package from "../plans/package";

export default function Pricing() {
  const brochureUrl = "https://hickter.com/download-brochure/Brochure-hickter.pdf";

  const brochureHighlights = [
    "Comprehensive Website & App Pricing Plans",
    "Enterprise Cloud Tech Stack & Architecture",
    "Digital Marketing Strategy & Deliverables",
    "Transparent Milestone Timelines & SLA"
  ];

  return (
    <div className="w-full max-w-[100vw] overflow-x-hidden min-h-screen bg-[#F8FAFC] dark:bg-[#0B0F19] text-[#0F172A] dark:text-[#F8FAFC] transition-colors duration-300 font-sans">
      
      {/* Section 1: Hero / Front Banner Package */}
      <section id="package-section" className="w-full max-w-[100vw] overflow-x-hidden">
        <Package />
      </section>

      {/* Section 2: Web Services Section */}
      <section id="web-services-section" className="w-full max-w-[100vw] overflow-x-hidden">
        <WebServices />
      </section>

      {/* Section 3: App Development Section */}
      <section id="app-dev-section" className="w-full max-w-[100vw] overflow-x-hidden">
        <AppDevelopment />
      </section>

      {/* Section 4: Tech Stack Section */}
      <section className="w-full max-w-[100vw] overflow-x-hidden">
        <Hometechused4 />
      </section>

      {/* Section 5: BEAUTIFUL ANIMATED CTA - DOWNLOAD BROCHURE SECTION */}
      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative overflow-hidden">
        
        {/* Background Glowing Ambient Orbs */}
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 -translate-x-1/2 w-96 h-96 bg-blue-600/15 dark:bg-blue-500/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 translate-x-1/2 w-96 h-96 bg-indigo-600/15 dark:bg-violet-500/20 rounded-full blur-[120px] pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative z-10 bg-gradient-to-br from-white via-slate-50 to-blue-50/60 dark:from-slate-900/95 dark:via-[#091124] dark:to-[#030712] border border-slate-200/90 dark:border-slate-800 rounded-3xl p-8 sm:p-12 lg:p-16 shadow-2xl shadow-blue-500/10 dark:shadow-none overflow-hidden"
        >
          {/* Decorative Corner Grid Pattern */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:16px_16px] opacity-20 pointer-events-none" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            
            {/* LEFT CONTENT COLUMN */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              
              {/* BADGE */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-cyan-400 text-xs font-bold uppercase tracking-wider">
                <Sparkles size={14} className="animate-spin text-amber-500 dark:text-amber-400" />
                <span>Official Hickter Capabilities Guide 2026</span>
              </div>

              {/* TITLE */}
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-[1.15]">
                Want Detailed Service Pricing &{" "}
                <span className="bg-gradient-to-r from-blue-600 via-cyan-500 to-indigo-600 dark:from-cyan-400 dark:via-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
                  Scope Breakdown?
                </span>
              </h2>

              {/* DESCRIPTION */}
              <p className="text-slate-600 dark:text-slate-300 text-sm sm:text-base leading-relaxed max-w-xl mx-auto lg:mx-0 font-medium">
                Get instant access to our official company brochure. Explore our end-to-end web & mobile engineering capabilities, client case studies, tech stack SLA, and transparent pricing packages in one PDF.
              </p>

              {/* HIGHLIGHT CHECKMARKS */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {brochureHighlights.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2.5 text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-200">
                    <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-cyan-400 flex items-center justify-center shrink-0">
                      <CheckCircle2 size={13} />
                    </div>
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              {/* ACTION BUTTONS CONTAINER */}
              <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                
                {/* PRIMARY DOWNLOAD BROCHURE BUTTON WITH SHINE ANIMATION */}
                <motion.a
                  href={brochureUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download="Brochure-hickter.pdf"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  className="relative group overflow-hidden h-14 px-8 rounded-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 dark:from-blue-600 dark:via-indigo-600 dark:to-blue-700 !text-white font-extrabold text-sm sm:text-base flex items-center justify-center gap-3 shadow-xl shadow-blue-600/30 transition-all cursor-pointer border border-blue-400/40"
                >
                  {/* Subtle shine overlay */}
                  <span className="absolute inset-0 w-full h-full bg-white/20 -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
                  
                  <motion.div
                    animate={{ y: [0, -3, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                  >
                    <Download size={20} className="!text-white shrink-0" />
                  </motion.div>
                  
                  <span className="!text-white font-black tracking-wide drop-shadow-sm select-none">
                    Download Brochure PDF
                  </span>
                </motion.a>

                {/* SECONDARY REQUEST PROPOSAL BUTTON */}
                <Link
                  to="/enquirynow"
                  className="h-14 px-8 rounded-2xl bg-white dark:bg-slate-800 text-slate-800 dark:text-white font-bold text-sm sm:text-base border border-slate-300 dark:border-slate-700 hover:border-blue-500/50 hover:bg-slate-100 dark:hover:bg-slate-700/80 flex items-center justify-center gap-2 transition-all cursor-pointer shadow-sm"
                >
                  <span>Request Custom Proposal</span>
                  <ArrowRight size={18} className="text-blue-600 dark:text-cyan-400" />
                </Link>

              </div>

            </div>

            {/* RIGHT VISUAL CARD COLUMN */}
            <div className="lg:col-span-5 flex justify-center">
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                className="w-full max-w-sm bg-white dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6 relative"
              >
                {/* Floating Top Badge */}
                <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-700/80">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-500/20 text-blue-600 dark:text-cyan-400 flex items-center justify-center">
                      <FileText size={20} />
                    </div>
                    <div>
                      <h4 className="text-sm font-extrabold text-slate-900 dark:text-white">Brochure-hickter.pdf</h4>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">Official Digital PDF • 2.4 MB</p>
                    </div>
                  </div>
                  <ShieldCheck size={20} className="text-emerald-500" />
                </div>

                {/* Document Highlights Mini Card */}
                <div className="space-y-3 bg-slate-50 dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                  <div className="flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                    <span>Document Status</span>
                    <span className="text-emerald-600 dark:text-emerald-400 font-extrabold flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      Verified 2026 Edition
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                    <span>File Format</span>
                    <span className="text-blue-600 dark:text-cyan-400 font-extrabold">PDF Document</span>
                  </div>
                  <div className="flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
                    <span>Access</span>
                    <span className="text-indigo-600 dark:text-purple-400 font-extrabold">Instant Free Download</span>
                  </div>
                </div>

                {/* Animated Download Badge Link */}
                <a
                  href={brochureUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download="Brochure-hickter.pdf"
                  className="w-full py-3.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 !text-white text-xs font-black flex items-center justify-between transition-all group/btn border border-blue-500 shadow-md shadow-blue-500/20"
                >
                  <span className="!text-white font-extrabold tracking-wide">Click to Download PDF</span>
                  <div className="w-7 h-7 rounded-lg bg-white/20 text-white flex items-center justify-center group-hover/btn:scale-110 transition-transform">
                    <Download size={14} className="!text-white" />
                  </div>
                </a>

              </motion.div>
            </div>

          </div>
        </motion.div>

      </section>

    </div>
  );
}
